import React, { useState, useEffect } from 'react';
import { labelStyle } from './KanbanCard';

const fieldClass =
  'w-full px-2.5 py-1.5 rounded-lg bg-neutral-800 border border-neutral-700/60 text-sm text-neutral-100 placeholder-neutral-500 outline-none focus:border-neutral-500 transition-colors';

const NEW_LABEL_VALUE = '__new_label__';

function formatFullDate(dateString) {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export default function KanbanCardModal({
  item,
  isEditable,
  columns,
  availableCategories,
  onClose,
  onSave,
  onMove,
  onDelete
}) {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || '');
  const [category, setCategory] = useState(item.category || '');
  const [isSaving, setIsSaving] = useState(false);
  // Custom entry mode kicks in when the card's current label isn't one of
  // the known options (e.g. was set before the option list existed), or
  // once the user picks "+ New label…" from the dropdown.
  const [isCustomLabel, setIsCustomLabel] = useState(
    Boolean(item.category) && !availableCategories.includes(item.category)
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isDirty =
    title.trim() !== item.title ||
    description.trim() !== (item.description || '') ||
    category.trim() !== (item.category || '');

  const handleSave = async () => {
    if (!title.trim() || isSaving) return;
    setIsSaving(true);
    try {
      await onSave(item.id, title.trim(), description.trim(), category.trim());
      onClose();
    } catch (err) {
      console.error('Error saving card:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-start justify-center bg-black/60 p-0 sm:p-4 sm:pt-[10vh] overflow-y-auto"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg max-h-[92dvh] sm:max-h-none overflow-y-auto rounded-t-2xl sm:rounded-xl bg-neutral-900 border border-neutral-800 shadow-2xl">
        {/* Drag handle affordance on mobile bottom sheet */}
        <div className="sm:hidden flex justify-center pt-2 pb-1" aria-hidden="true">
          <div className="w-10 h-1 rounded-full bg-neutral-700" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 pt-2 sm:pt-4">
          <div className="flex-1 min-w-0">
            {isEditable ? (
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Card title"
                className="w-full bg-transparent text-base font-medium text-neutral-100 placeholder-neutral-500 outline-none border-b border-transparent focus:border-neutral-600 pb-0.5 transition-colors"
              />
            ) : (
              <h2 className="text-base font-medium text-neutral-100 break-words">
                {item.title}
              </h2>
            )}
            <div className="mt-1 text-xs text-neutral-500">
              in <span className="text-neutral-400">{item.column}</span>
              {item.category && (
                <span className={`ml-2 px-1.5 py-px rounded text-[10px] font-medium ${labelStyle(item.category)}`}>
                  {item.category}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-9 h-9 sm:w-7 sm:h-7 flex items-center justify-center rounded-lg text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 transition-colors touch-manipulation"
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          <div>
            <div className="text-xs font-medium text-neutral-400 mb-1.5">Description</div>
            {isEditable ? (
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a more detailed description…"
                rows="4"
                className={`${fieldClass} resize-none`}
              />
            ) : (
              <p className="text-sm text-neutral-300 whitespace-pre-wrap break-words">
                {item.description || <span className="text-neutral-600">No description</span>}
              </p>
            )}
          </div>

          {isEditable && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-medium text-neutral-400 mb-1.5">Label</div>
                {isCustomLabel ? (
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      placeholder="New label"
                      autoFocus
                      className={fieldClass}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setIsCustomLabel(false);
                        setCategory('');
                      }}
                      className="shrink-0 px-2 rounded-lg text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 transition-colors touch-manipulation"
                      title="Back to label list"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <select
                    value={category || ''}
                    onChange={(e) => {
                      if (e.target.value === NEW_LABEL_VALUE) {
                        setIsCustomLabel(true);
                        setCategory('');
                      } else {
                        setCategory(e.target.value);
                      }
                    }}
                    className={`${fieldClass} appearance-none cursor-pointer`}
                  >
                    <option value="">None</option>
                    {availableCategories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value={NEW_LABEL_VALUE}>+ New label…</option>
                  </select>
                )}
              </div>
              <div>
                <div className="text-xs font-medium text-neutral-400 mb-1.5">List</div>
                <select
                  value={item.column}
                  onChange={(e) => onMove(item.id, e.target.value)}
                  className={`${fieldClass} appearance-none cursor-pointer`}
                >
                  {columns.map((col) => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500">
            {item.createdAt && <span>Created {formatFullDate(item.createdAt)}</span>}
            {item.finishedAt && (
              <span className="text-emerald-400/80">Finished {formatFullDate(item.finishedAt)}</span>
            )}
          </div>
        </div>

        {/* Footer */}
        {isEditable && (
          <div className="flex items-center justify-between gap-3 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-4">
            <button
              onClick={() => onDelete(item.id)}
              className="px-2.5 py-2 sm:py-1.5 rounded-lg text-xs text-rose-400/80 hover:text-rose-300 hover:bg-rose-500/10 transition-colors touch-manipulation"
            >
              Delete card
            </button>
            <button
              onClick={handleSave}
              disabled={!title.trim() || !isDirty || isSaving}
              className="px-4 py-2 sm:px-3 sm:py-1.5 rounded-lg bg-neutral-200 hover:bg-white text-neutral-900 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
